import React from "react";
import PropTypes from "prop-types";

/**
 * Button
 * - Backwards-compatible with earlier simple Button.
 * - Also exported as named `Button` for consumers that import { Button }.
 */
const Button = ({
  text,
  children,
  onClick,
  className,
  type = "button",
  disabled = false,
  variant = "primary", // primary | outline | ghost
  size = "md", // sm | md | lg
  ariaLabel,
  ...rest
}) => {
  const base = "inline-flex items-center justify-center gap-2 font-semibold rounded-md transition focus:outline-none focus:ring-2 focus:ring-offset-2";
  const sizes = {
    sm: "px-2 py-1 text-sm rounded-md",
    md: "px-4 py-2 text-sm rounded-md",
    lg: "px-5 py-3 text-base rounded-lg",
  };
  const variants = {
    primary: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 disabled:bg-blue-300 disabled:cursor-not-allowed",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-400 disabled:opacity-50",
    ghost: "bg-transparent text-gray-700 hover:bg-gray-100 focus:ring-gray-300",
  };

  const composedClass = `${base} ${sizes[size] ?? sizes.md} ${variants[variant] ?? variants.primary} ${className ?? ""}`;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={composedClass}
      aria-label={ariaLabel}
      {...rest}
    >
      {text ? text : children}
    </button>
  );
};

Button.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node,
  onClick: PropTypes.func,
  className: PropTypes.string,
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf(["primary", "outline", "ghost"]),
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  ariaLabel: PropTypes.string,
};

Button.defaultProps = {
  onClick: () => {},
  className: "rounded bg-blue-500 text-white hover:bg-blue-600",
  disabled: false,
  variant: "primary",
  size: "md",
};

// Named export for compatibility with `import { Button } from '...'`
export { Button };

// Default export for compatibility with `import Button from '...'`
export default Button;
